import { ComponentProps, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailDetailsActionLocation from '@alltrails/amplitude/enums/TrailDetailsActionLocation';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsAddToListButtonTapped from '@alltrails/amplitude/events/logTrailDetailsAddToListButtonTapped';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import IconButton from '@alltrails/denali/components/IconButton';
import Heart from '@alltrails/denali/icons/Heart';
import HeartFilled from '@alltrails/denali/icons/HeartFilled';
import type { Size } from '@alltrails/denali/types';
import { useExposureEvent } from '@alltrails/experiments';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import SaveSuccessToast from '@/components/SaveSuccessToast/SaveSuccessToast';
import useSaveSuccessToast from '@/components/SaveSuccessToast/useSaveSuccessToast';

type SaveCtaProps = {
  buttonLocation?: TrailDetailsActionLocation;
  size?: Size<'sm' | 'md'>;
  trailId: number;
  trailPageType: TrailPageType;
  variant?: ComponentProps<typeof IconButton>['variant'];
};

const SaveCta = ({ buttonLocation, trailId, variant, size = 'md', trailPageType }: SaveCtaProps) => {
  const intl = useIntl();
  const sendSaveToastExposure = useExposureEvent('web-growth-rm-save-toast');
  const { isOpen, onSaveToListModalClose, savedLists, setIsOpen } = useSaveSuccessToast();

  const reduxListsAnalyticsInfo = useMemo(
    () => ({
      detailedCardLocation: 'trail banner' as CardLocation,
      page: PageStrings.TRAIL_PAGE,
      trailPageType
    }),
    [trailPageType]
  );

  const { isFavorite, handleFavoriteClick, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo,
    onSaveToListModalClose
  );

  const isTrailFavorite = isFavorite('trail', trailId);

  const onFavoriteClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'save',
      trail_page_location: buttonLocation,
      trail_page_type: trailPageType
    });
    logTrailDetailsAddToListButtonTapped({ trail_id: trailId });
    sendSaveToastExposure();

    handleFavoriteClick('trail', trailId);
  }, [buttonLocation, handleFavoriteClick, sendSaveToastExposure, trailId, trailPageType]);

  return (
    <>
      <IconButton
        icon={{ Component: isTrailFavorite ? HeartFilled : Heart }}
        onClick={onFavoriteClick}
        size={size}
        title={intl.formatMessage({ defaultMessage: 'Save' })}
        variant={variant}
      />
      {listModal}
      {signUpModal}
      <SaveSuccessToast isOpen={isOpen} savedLists={savedLists} setIsOpen={setIsOpen} />
    </>
  );
};

export default SaveCta;
